import styles from "./Carts.module.css";
import ordersStyles from "../../Orders/Orders.module.css";
import { API } from "../../../config";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Spin from "../../Spinner/Spinner";
import { GoPlus } from "react-icons/go";
import { FiCopy } from "react-icons/fi";
import { CreateCartDialog } from "./CreateCartDialog";
import { BiTransferAlt } from "react-icons/bi";
import { toast } from "react-toastify";
import { TransferCartDialog } from "./TransferCartDialog";

export const UserCarts = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const [userDetails, setUserDetails] = useState(null);
  const [showCreateCart, setShowCreateCart] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedCartId, setSelectedCartId] = useState(null);
  const navigate = useNavigate();

  const statusColors = {
    fulfilled: "#03ce00",
    cancelled: "#e12222",
    processing: "#ffa500",
    "active-cart": "#6687fa",
  };

  const formatPrice = (amount) => {
    if (amount == null) return "-";
    const rounded = Math.round(amount);
    return `₹${rounded.toLocaleString("en-IN")}/-`;
  };

  const getOrders = async () => {
    try {
      const response = await axios.get(`${API}/Admin/cartOrders/user/${id}`);
      setOrders(response.data.orders || []);
    } catch (error) {
      console.log(error);
      toast.error("Failed to get orders");
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const getUserDetails = async () => {
    setLoading(true);
    const response = await axios.get(`${API}/Admin/users/${id}`);
    setUserDetails(response.data);
    setLoading(false);
  };
  useEffect(() => {
    getUserDetails();
    getOrders();
  }, []);

  const duplicateCart = async (cartId) => {
    try {
      await axios.put(`${API}/Admin/carts/${cartId}/duplicate`);
      toast.success("Cart duplicated successfully");
      getUserDetails();
    } catch (error) {
      console.error("Failed to duplicate cart:", error);
      toast.error("Failed to duplicate cart");
    }
  };

  const transferCart = async (cartId, userId) => {
    try {
      await axios.put(`${API}/Admin/carts/${cartId}/transfer/${userId}`);
      toast.success("Cart transferred successfully");
      getUserDetails();
    } catch (error) {
      console.error("Failed to transfer cart:", error);
      toast.error("Failed to transfer cart");
    }
  };

  const CartItems = useMemo(() => {
    if (!userDetails?.carts || loading) return [];
    const cartItems = userDetails.carts?.map((cart) => {
      const cartDiscount = cart.discount ?? 0;
      const pricePerBox = cart?.products?.reduce((acc, p) => {
        const productPrice = p.cost ?? 0;
        return (
          acc +
          ((productPrice ?? 0) - (productPrice ?? 0) * (cartDiscount / 100)) *
            (p.quantity ?? 0)
        );
      }, 0);
      return {
        name: cart?.name || userDetails?.user?.companyName || "-",
        itemsCount: cart.products.length ?? 0,
        pricePerBox: pricePerBox,
        totalPrice: pricePerBox,
        cartId: cart._id,
        createdAt: formatDate(cart.createdAt),
        updatedAt: formatDate(cart.updatedAt),
      };
    });
    return cartItems;
  }, [userDetails?.carts, loading]);
  if (loading) {
    return <Spin />;
  }
  if (!userDetails) {
    return <div>User not found</div>;
  }
  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <h1>{userDetails?.user?.name ?? "-"}'s Active Carts</h1>
        <button className={styles.button}>
          <GoPlus />
          <span
            onClick={() => {
              setShowCreateCart(true);
            }}
          >
            Create New
          </span>
        </button>
      </div>
      <div className={styles.cartItemsContainer}>
        {CartItems.map((item) => (
          <CartCard
            key={item.name}
            {...item}
            onDuplicateCart={() => duplicateCart(item.cartId)}
            onTransferCart={() => {
              setSelectedCartId(item.cartId);
              setShowTransferDialog(true);
            }}
          />
        ))}
      </div>
      <div className={ordersStyles.ordersTableContainer}>
        <h2>Orders</h2>
        {(!orders || orders.length === 0) && (
          <p className={ordersStyles.noOrders}>No orders found</p>
        )}

        {orders && orders.length > 0 && (
          <table className={ordersStyles.ordersTable}>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>User</th>
                <th>Date</th>
                <th>Status</th>
                <th>Products</th>
                <th>Total Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const shortId = order?._id
                  ? order._id.slice(-4).toUpperCase()
                  : "";
                const status = order?.status || "processing";
                const pillColor =
                  statusColors[status] || statusColors["processing"];
                return (
                  <tr key={order._id}>
                    <td>{shortId ? `HM-${shortId}` : "-"}</td>
                    <td>{order.userName || order.userCompanyName || "-"}</td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      <span
                        className={ordersStyles.statusPill}
                        style={{ backgroundColor: pillColor }}
                      >
                        {status}
                      </span>
                    </td>
                    <td>{order.totalProducts ?? 0}</td>
                    <td>{formatPrice(order.amount)}</td>
                    <td>
                      <button
                        type="button"
                        className={ordersStyles.viewDetailsButton}
                        onClick={() => navigate(`/orders/${order._id}`)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <CreateCartDialog
        show={showCreateCart}
        onClose={() => setTimeout(() => setShowCreateCart(false), 0)}
        userId={userDetails?.user?._id}
        onCreated={() => {
          toast.dismiss();
          getUserDetails();
        }}
      />
      <TransferCartDialog
        show={showTransferDialog}
        onClose={() => {
          setShowTransferDialog(false);
          setSelectedCartId(null);
        }}
        onSave={(userId) => {
          if (!selectedCartId) return;
          transferCart(selectedCartId, userId);
          setShowTransferDialog(false);
          setSelectedCartId(null);
        }}
      />
    </div>
  );
};

const CartCard = ({
  name,
  itemsCount,
  pricePerBox,
  totalPrice,
  cartId,
  createdAt,
  updatedAt,
  onDuplicateCart,
  onTransferCart,
}) => {
  const navigate = useNavigate();
  const formatPrice = (price) => {
    if (price == null || price === undefined) return "-";
    const rounded = Math.round(price);
    return `₹${rounded.toLocaleString("en-IN")}/-`;
  };

  const data = [
    {
      title: "Name",
      value: name,
    },
    {
      title: "No. of items",
      value: itemsCount,
    },
    {
      title: "Price per box",
      value: formatPrice(pricePerBox),
    },
    {
      title: "Total Amount",
      value: formatPrice(totalPrice),
    },
    {
      title: "Created At",
      value: createdAt,
    },
    {
      title: "Updated At",
      value: updatedAt,
    },
  ];
  return (
    <div className={styles.cartCard}>
      <div className={styles.textContainer}>
        <div className={styles.text}>
          {data.map((item) => (
            <div
              key={item.title}
              style={{
                width: "120px",
              }}
            >
              <h4
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.title}
              </h4>
              <h1
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.value ?? "-"}
              </h1>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            className={styles.textualButton}
            onClick={() => navigate(`${cartId}`)}
          >
            View Details
          </button>
          <div className={styles.duplicateIconWrapper}>
            <button
              type="button"
              className={`${styles.textualButton} ${styles.duplicateIconButton}`}
              aria-label="Duplicate cart"
              onClick={() => onDuplicateCart()}
            >
              <FiCopy />
            </button>
            <span className={styles.duplicateTooltip}>Duplicate cart</span>
          </div>
          <div className={styles.duplicateIconWrapper}>
            <button
              type="button"
              className={`${styles.textualButton} ${styles.duplicateIconButton}`}
              aria-label="Transfer cart"
              onClick={() => onTransferCart()}
            >
              <BiTransferAlt />
            </button>
            <span className={styles.duplicateTooltip}>Transfer cart</span>
          </div>
        </div>
      </div>
    </div>
  );
};
